import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

const REACT_APP_STATIC_URL = process.env.REACT_APP_STATIC_URL;

const Alert = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 0C8.85652 0 10.637 0.737498 11.9497 2.05025C13.2625 3.36301 14 5.14348 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14C5.14348 14 3.36301 13.2625 2.05025 11.9497C0.737498 10.637 0 8.85652 0 7C0 5.14348 0.737498 3.36301 2.05025 2.05025C3.36301 0.737498 5.14348 0 7 0ZM7 2.69231C6.5524 2.69231 6.19231 3.0524 6.19231 3.5V7.26923C6.19231 7.71683 6.5524 8.07692 7 8.07692C7.4476 8.07692 7.80769 7.71683 7.80769 7.26923V3.5C7.80769 3.0524 7.4476 2.69231 7 2.69231ZM8.07692 10.2308C8.07692 9.94515 7.96346 9.67123 7.7615 9.46927C7.55954 9.26731 7.28562 9.15385 7 9.15385C6.71438 9.15385 6.44046 9.26731 6.2385 9.46927C6.03654 9.67123 5.92308 9.94515 5.92308 10.2308C5.92308 10.5164 6.03654 10.7903 6.2385 10.9923C6.44046 11.1942 6.71438 11.3077 7 11.3077C7.28562 11.3077 7.55954 11.1942 7.7615 10.9923C7.96346 10.7903 8.07692 10.5164 8.07692 10.2308Z"
        fill="#E54344"
      />
    </svg>
  );
};

const Warning = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6 12C7.5913 12 9.11742 11.3679 10.2426 10.2426C11.3679 9.11742 12 7.5913 12 6C12 4.4087 11.3679 2.88258 10.2426 1.75736C9.11742 0.632141 7.5913 0 6 0C4.4087 0 2.88258 0.632141 1.75736 1.75736C0.632141 2.88258 0 4.4087 0 6C0 7.5913 0.632141 9.11742 1.75736 10.2426C2.88258 11.3679 4.4087 12 6 12ZM5.0625 7.875H5.625V6.375H5.0625C4.75078 6.375 4.5 6.12422 4.5 5.8125C4.5 5.50078 4.75078 5.25 5.0625 5.25H6.1875C6.49922 5.25 6.75 5.50078 6.75 5.8125V7.875H6.9375C7.24922 7.875 7.5 8.12578 7.5 8.4375C7.5 8.74922 7.24922 9 6.9375 9H5.0625C4.75078 9 4.5 8.74922 4.5 8.4375C4.5 8.12578 4.75078 7.875 5.0625 7.875ZM6 3C6.19891 3 6.38968 3.07902 6.53033 3.21967C6.67098 3.36032 6.75 3.55109 6.75 3.75C6.75 3.94891 6.67098 4.13968 6.53033 4.28033C6.38968 4.42098 6.19891 4.5 6 4.5C5.80109 4.5 5.61032 4.42098 5.46967 4.28033C5.32902 4.13968 5.25 3.94891 5.25 3.75C5.25 3.55109 5.32902 3.36032 5.46967 3.21967C5.61032 3.07902 5.80109 3 6 3Z"
        fill="#FF8C00"
      />
    </svg>
  );
};

const NoRisk = () => {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7 0C8.85652 0 10.637 0.737498 11.9497 2.05025C13.2625 3.36301 14 5.14348 14 7C14 8.85652 13.2625 10.637 11.9497 11.9497C10.637 13.2625 8.85652 14 7 14C5.14348 14 3.36301 13.2625 2.05025 11.9497C0.737498 10.637 0 8.85652 0 7C0 5.14348 0.737498 3.36301 2.05025 2.05025C3.36301 0.737498 5.14348 0 7 0ZM10.8029 5.41827C11.1192 5.10192 11.1192 4.59039 10.8029 4.2774C10.4865 3.96442 9.975 3.96106 9.66202 4.2774L5.92644 8.01298L4.34471 6.43125C4.02837 6.1149 3.51683 6.1149 3.20385 6.43125C2.89087 6.7476 2.8875 7.25913 3.20385 7.57212L5.35769 9.72596C5.67404 10.0423 6.18558 10.0423 6.49856 9.72596L10.8029 5.41827Z"
        fill="#27CA40"
      />
    </svg>
  );
};

const ResultsHeader = ({ request, checks, isLoading, setShowDeleteModal }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.between("mobile", "ipad"));
  const tablet = useMediaQuery(theme.breakpoints.between("ipad", "laptop"));
  const isMaatwerk = window.location.hostname.includes("maatwerk");
  const isWelocate = window.location.hostname.includes("welocate");
  const isDemo = window.location.hostname.includes("demo");

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "min(100%, 95vw)",
        marginBottom: mobile ? "0px" :"12px",
      }}
    >
      <Box
        sx={{
          height: "180px",
          background: "#000",
          borderRadius: "12px",
        }}
      >
        <img
          src={`${REACT_APP_STATIC_URL}/img/wonen.png`}
          alt="Header Bg"
          style={{
            width: "100%",
            height: "180px",
            objectFit: "cover",
            objectPosition: mobile ? "center center" : tablet ? "0px -450px" : "0px -860px",
            opacity: 0.7,
            borderRadius: "12px",
          }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: mobile ? "column" : "row",
            justifyContent: "space-between",
            background: "#fff",
            borderRadius: "12px",
            width: "90%",
            padding: "18px 24px",
            boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
            marginTop: "-50px",
            zIndex: 10,
            gap: mobile ? "18px" : "40px",
          }}
        >
          {/* Links */}
          <Box sx={{ display: "flex", flex: 1, gap: "16px", minWidth: "250px" }}>
            {!isLoading && (
              <React.Fragment>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "64px",
                    height: "64px",
                    borderRadius: "100px",
                    padding: "12px",
                    background: "#439BD9",
                  }}
                >
                  <img
                    src={`${REACT_APP_STATIC_URL}/icons/${request?.module?.iconUrl}.svg`}
                    alt="Module Icon"
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginTop: "2px",
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: "24px",
                      margin: 0,
                      padding: 0,
                      color: "#5C5C5C",
                      lineHeight: "1.6",
                    }}
                  >
                    {t(`module.${request?.module?.name}`)}
                  </Typography>
                  <Typography
                    sx={{ fontWeight: 400, color: "#5C5C5C", fontSize: "14px" }}
                  >
                    {t("yourCheckResults")}
                  </Typography>
                </Box>
              </React.Fragment>
            )}
          </Box>

          {/* Midden  */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              width: mobile ? "100%" : "fit-content",
              gap: "6px",
              flex: 1,
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  alignItems: "center",
                  height: "64px",
                }}
              >
                <Typography sx={{ fontSize: "14px" }}>
                  {t("resultatenWordenGeladen")}
                </Typography>
              </Box>
            ) : (
              <React.Fragment>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: "16px",
                    fontWeight: 600,
                    margin: 0,
                    padding: 0,
                    color: "#5C5C5C",
                    lineHeight: "1.6",
                  }}
                >
                  {t("Resultaten")}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    gap: "20px",
                  }}
                >
                  <Box
                    sx={{ display: "flex", gap: "6px", alignItems: "center" }}
                  >
                    <NoRisk />
                    <Typography sx={{ color: "#28C76F", fontWeight: 700 }}>
                      {
                        checks.filter(
                          (check) =>
                            check.textInstanceResults.length !== 0 &&
                            check.textInstanceResults.some(
                              (result) =>
                                result.variableOptionInstance.riskLevel === 0
                            )
                        ).length
                      }
                    </Typography>
                  </Box>

                  <Box
                    sx={{ display: "flex", gap: "6px", alignItems: "center" }}
                  >
                    <Warning />
                    <Typography sx={{ color: "#E17C00", fontWeight: 700 }}>
                      {
                        checks.filter(
                          (check) =>
                            check.textInstanceResults.length !== 0 &&
                            check.textInstanceResults.some(
                              (result) =>
                                result.variableOptionInstance.riskLevel === 1
                            )
                        ).length
                      }
                    </Typography>
                  </Box>
                  <Box
                    sx={{ display: "flex", gap: "6px", alignItems: "center" }}
                  >
                    <Alert />
                    <Typography sx={{ color: "#E54344", fontWeight: 700 }}>
                      {
                        checks.filter(
                          (check) =>
                            check.textInstanceResults.length !== 0 &&
                            check.textInstanceResults.some(
                              (result) =>
                                result.variableOptionInstance.riskLevel === 2
                            )
                        ).length
                      }
                    </Typography>
                  </Box>
                </Box>
              </React.Fragment>
            )}
          </Box>

          {/* Rechts */}
          <Box
            sx={{
              display: mobile ? "none" : "flex",
              flexDirection: "column",
              alignItems: "end",
              justifyContent: "center",
              flex: 1,
              gap: "12px",
            }}
          >
            {!!request?.moduleDocumentUploads && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <svg
                  width="13"
                  height="18"
                  viewBox="0 0 13 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2.16667 16.3125C1.86875 16.3125 1.625 16.0594 1.625 15.75V2.25C1.625 1.94062 1.86875 1.6875 2.16667 1.6875H7.58333V4.5C7.58333 5.12227 8.06745 5.625 8.66667 5.625H11.375V15.75C11.375 16.0594 11.1313 16.3125 10.8333 16.3125H2.16667ZM2.16667 0C0.971615 0 0 1.00898 0 2.25V15.75C0 16.991 0.971615 18 2.16667 18H10.8333C12.0284 18 13 16.991 13 15.75V5.43164C13 4.83398 12.7732 4.26094 12.3669 3.83906L9.29974 0.657422C8.89349 0.235547 8.34505 0 7.76953 0H2.16667ZM9.78385 9.40781C10.1021 9.07734 10.1021 8.54297 9.78385 8.21602C9.46563 7.88906 8.95104 7.88555 8.6362 8.21602L5.59948 11.3695L4.36719 10.0898C4.04896 9.75938 3.53438 9.75938 3.21953 10.0898C2.90469 10.4203 2.9013 10.9547 3.21953 11.2816L5.02396 13.1555C5.1763 13.3137 5.38281 13.4016 5.59948 13.4016C5.81615 13.4016 6.02266 13.3137 6.175 13.1555L9.78385 9.40781Z"
                    fill="#5C5C5C"
                  />
                </svg>

                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#5C5C5C",
                    maxWidth: "250px",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                  }}
                >
                  {request?.moduleDocumentUploads?.length === 0
                    ? ""
                    : request?.moduleDocumentUploads[0].originalFileName}
                </Typography>
              </Box>
            )}

            <Box
              sx={{
                display: mobile || tablet ? "none" : "block",
              }}
            >
              {isMaatwerk || isWelocate || isDemo ? (
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: "500",
                    color: "#5C5C5C",
                    fontStyle: "italic",
                  }}
                >
                  {t("demoAutoDelete")}
                </Typography>
              ) : (
                <Button
                  variant="outlined"
                  sx={{
                    border: "1px solid #E54344",
                    background: "none",
                    textTransform: "none",
                    color: "#E54344",
                    borderRadius: "40px",
                    height: "27px",
                    fontSize: "10px",
                    fontWeight: "700",
                  }}
                  disabled={isLoading}
                  onClick={() => setShowDeleteModal(true)}
                >
                  {t("Verwijderen")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ResultsHeader;
